import { useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import { ArrowDownToLine, IterationCw } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { NodeStruct } from '@/types/node-types';
import { FOR_EACH_EDGE_COLOR } from '@/constants/node-styling';
import {
  canBeInForEach,
  forEachItemSlot,
  isMapItemEdge,
  MAP_COLLECT_TARGET_HANDLE,
  MAP_ITEM_SOURCE_HANDLE,
  MAP_ITEM_TARGET_HANDLE_PREFIX,
  mapItemTargetHandleId,
} from '@/pages/workflow/helper/for-each-region';
import { cn } from '@/lib/utils';

interface ForEachHandleProps {
  nodeId: string;
  struct: NodeStruct;
  isLocked?: boolean;
}

/**
 * Where "each item" lands: one target handle on a node that can run per
 * item. Hidden until the Map's "each item" handle is being dragged or the
 * edge is connected, so ordinary nodes stay clean.
 */
export function ForEachItemHandle({
  nodeId,
  struct,
  isLocked = false,
}: ForEachHandleProps) {
  const { activeConnect, groupedNodes, connectedKey } = useNodeStore(
    useShallow((state) => ({
      activeConnect: state.activeConnect,
      groupedNodes: state.groupedNodes,
      connectedKey: state.edges
        .filter((edge) => edge.target === nodeId && isMapItemEdge(edge))
        .map((edge) => edge.targetHandle)
        .join('|'),
    })),
  );
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const slot = forEachItemSlot(struct, catalog);
  if (!slot) return null;
  const handleId = mapItemTargetHandleId(slot);
  const dragActive =
    activeConnect?.side === 'source' &&
    activeConnect.handleId === MAP_ITEM_SOURCE_HANDLE &&
    activeConnect.nodeId !== nodeId;
  const visible = dragActive || connectedKey.split('|').includes(handleId);

  return (
    <Handle
      id={handleId}
      type='target'
      position={Position.Top}
      isConnectable={!isLocked}
      isConnectableStart={false}
      title={`Item input "${slot.target_id}": drop "each item" here`}
      data-testid={`for-each-item-target-${nodeId}`}
      style={{
        top: -7,
        left: 'auto',
        right: 28,
        transform: 'none',
        width: 14,
        height: 14,
        borderRadius: 999,
        backgroundColor: FOR_EACH_EDGE_COLOR,
        color: FOR_EACH_EDGE_COLOR,
        border: '2px solid var(--color-card)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      className='for-each-item-handle transition-opacity duration-150'
      isValidConnection={(connection) =>
        connection.sourceHandle === MAP_ITEM_SOURCE_HANDLE
      }
    />
  );
}

/**
 * The two Map rows that make a For each: "each item" (drag to the first
 * step) and "collect" (drag the last step's output back here). A Map with
 * neither keeps working as a single-step Map.
 */
export function ForEachRows({
  nodeId,
  isLocked = false,
}: Omit<ForEachHandleProps, 'struct'>) {
  const { activeConnect, groupedNodes, nodes } = useNodeStore(
    useShallow((state) => ({
      activeConnect: state.activeConnect,
      groupedNodes: state.groupedNodes,
      nodes: state.nodes,
    })),
  );
  const draggingItem =
    activeConnect?.side === 'source' &&
    activeConnect.nodeId === nodeId &&
    activeConnect.handleId === MAP_ITEM_SOURCE_HANDLE;
  const sourceStruct =
    activeConnect?.side === 'source' && activeConnect.nodeId !== nodeId
      ? nodes.find((node) => node.id === activeConnect.nodeId)?.data?.struct
      : undefined;
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const collectHighlighted =
    Boolean(sourceStruct) && canBeInForEach(sourceStruct, catalog);

  return (
    <div className='mt-1 space-y-1' data-testid='for-each-rows'>
      <div
        className={cn(
          'relative mx-3 flex items-center rounded-md border border-teal-300 dark:border-teal-500/50 bg-teal-50 dark:bg-teal-500/10 px-2 py-1.5 text-[11px] font-semibold text-teal-900 dark:text-teal-100',
          draggingItem && 'ring-2 ring-teal-300 dark:ring-teal-500/50',
        )}
        data-testid='for-each-item-row'
      >
        <span className='flex items-center gap-1'>
          <IterationCw className='size-3' />
          each item
          <span className='font-normal text-teal-800/80 dark:text-teal-200/80'>
            → the first step
          </span>
        </span>
        <Handle
          id={MAP_ITEM_SOURCE_HANDLE}
          type='source'
          position={Position.Right}
          isConnectable={!isLocked}
          title='Each item: drag to the first step of the part to run per item'
          data-testid='for-each-item-source-handle'
          className='!right-[-9px] !size-4 !border-2 !border-white dark:!border-zinc-900 !bg-teal-500'
          isValidConnection={(connection) =>
            Boolean(
              connection.targetHandle?.startsWith(
                MAP_ITEM_TARGET_HANDLE_PREFIX,
              ),
            )
          }
        />
      </div>
      <div
        className={cn(
          'relative mx-3 flex items-center rounded-md border border-dashed border-teal-400 dark:border-teal-500/60 bg-teal-50 dark:bg-teal-500/10 px-2 py-1.5 text-[11px] font-semibold text-teal-900 dark:text-teal-100',
          collectHighlighted && 'ring-2 ring-teal-300 dark:ring-teal-500/50',
        )}
        data-testid='for-each-collect-row'
      >
        <Handle
          id={MAP_COLLECT_TARGET_HANDLE}
          type='target'
          position={Position.Left}
          isConnectable={!isLocked}
          isConnectableStart={false}
          title="Collect: drag the last step's output here"
          data-testid='for-each-collect-target-handle'
          className='!left-[-9px] !size-4 !border-2 !border-white dark:!border-zinc-900 !bg-teal-500'
        />
        <span className='flex items-center gap-1'>
          <ArrowDownToLine className='size-3' />
          collect
          <span className='font-normal text-teal-800/80 dark:text-teal-200/80'>
            ← the last step’s output
          </span>
        </span>
      </div>
    </div>
  );
}
